/* eslint-disable react/prop-types */
import React, { Fragment, useEffect, useState } from 'react';
import { color as handleColor, validHex, hexToHsva, hsvaToHex, hsvaToHexa } from './ColorUtils';
import Placement from './SwatchPlacement';
import Saturation from './Saturation';
import Hue from './Hue';
import EditableColorInput from './EditableColorInput';
import EditableInputRGB from './EditableInputRGB';
import EditableInputHSL from './EditableInputHSL';
import Arrow from './Arrow';

export const PickerInputType = {
  HEX: 'hex',
  RGB: 'rgb',
  HSL: 'hsl',
};

const ColorPicker = React.forwardRef((props, ref) => {
  const {
    prefixCls = 'saws-color-picker',
    className,
    style,
    color,
    inputType = PickerInputType.RGB,
    onChange,
    includeAlpha = false,
    ...other
  } = props;

  const hsva = typeof color === 'string' && validHex(color) ? hexToHsva(color) : color || { h: 0, s: 0, l: 0 };

  const handleChange = (hsv) => onChange && onChange(handleColor(hsv), type);

  const [type, setType] = useState(inputType);

  const handleClick = () => {
    if (type === PickerInputType.RGB) {
      setType(PickerInputType.HSL);
    }
    if (type === PickerInputType.HSL) {
      setType(PickerInputType.HEX);
    }
    if (type === PickerInputType.HEX) {
      setType(PickerInputType.RGB);
    }
  };

  const labelStyle = { paddingTop: 6 };
  const inputStyle = { textAlign: 'center', paddingTop: 4, paddingBottom: 4 };

  useEffect(() => {
    handleChange(hsva, type);
  }, [type]);

  return (
    <Placement
      ref={ref}
      color={hsva}
      style={{
        borderRadius: 0,
        flexDirection: 'column',
        width: 230,
        padding: 0,
        ...style,
      }}
      className={[prefixCls, className].filter(Boolean).join(' ')}
      placement={false}
      {...other}
      addonAfter={
        <Fragment>
          <Saturation
            hsva={hsva}
            style={{ width: '100%', height: 130 }}
            onChange={(newColor) => {
              handleChange({ ...hsva, ...newColor, a: hsva.a });
            }}
          />
          <div style={{ padding: 15, display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Hue
                hue={hsva.h}
                style={{ width: '100%' }}
                bgProps={{
                  style: { borderRadius: 2 },
                }}
                onChange={(newHue) => {
                  let res = handleChange({ ...hsva, ...newHue });
                }}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: '0 15px 15px 15px',
              userSelect: 'none',
            }}
          >
            <div style={{ flex: 1 }}>
              {type === PickerInputType.RGB && (
                <EditableInputRGB
                  hsva={hsva}
                  rProps={{ labelStyle, inputStyle }}
                  gProps={{ labelStyle, inputStyle }}
                  bProps={{ labelStyle, inputStyle }}
                  aProps={includeAlpha ? { labelStyle, inputStyle } : false}
                  onChange={(reColor) => handleChange(reColor.hsva)}
                />
              )}
              {type === PickerInputType.HEX && (
                <EditableColorInput
                  label="HEX"
                  labelStyle={labelStyle}
                  inputStyle={inputStyle}
                  value={includeAlpha ? hsvaToHexa(hsva).toLocaleLowerCase() : hsvaToHex(hsva).toLocaleLowerCase()}
                  onChange={(_, value) => {
                    if (typeof value === 'string') {
                      handleChange(hexToHsva(/^#/.test(value) ? value : `#${value}`));
                    }
                  }}
                />
              )}
              {type === PickerInputType.HSL && (
                <EditableInputHSL
                  hsva={hsva}
                  hProps={{ labelStyle, inputStyle }}
                  sProps={{ labelStyle, inputStyle }}
                  lProps={{ labelStyle, inputStyle }}
                  aProps={includeAlpha ? { labelStyle, inputStyle } : false}
                  onChange={(reColor) => handleChange(reColor.hsva)}
                />
              )}
            </div>
            <Arrow onClick={handleClick} />
          </div>
        </Fragment>
      }
      rectRender={({ key }) => <Fragment key={key} />}
    />
  );
});

ColorPicker.displayName = 'ColorPicker';

export default ColorPicker;
